import type { OutreachStageMix } from '@discovr/types';
import { describeBreakdown, formatSplitPercent } from './crmFormat';
import { CrmCard, CrmCardNote } from './CrmCard';
import { stageSegments, type StageSegmentModel } from './crmOverviewModel';

export const STAGE_MIX_TITLE = 'By stage right now';
export const STAGE_MIX_UNAVAILABLE = 'Stages show here once the server is updated.';

/** One stacked bar and its legend: the five buckets partition every creator once, so the legend adds up to 100%. */
export function CrmStageMixCard({ mix }: { mix: OutreachStageMix | null | undefined }) {
  return (
    <CrmCard title={STAGE_MIX_TITLE}>
      {mix ? <StageMix mix={mix} /> : <CrmCardNote>{STAGE_MIX_UNAVAILABLE}</CrmCardNote>}
    </CrmCard>
  );
}

const hasCreators = (segment: StageSegmentModel) => segment.count > 0;

function StageMix({ mix }: { mix: OutreachStageMix }) {
  const segments = stageSegments(mix);
  return (
    <>
      <div role="img" aria-label={describeBreakdown(STAGE_MIX_TITLE, segments)} className="flex h-8 gap-0.5 overflow-hidden rounded-md">
        {segments.filter(hasCreators).map((segment) => (
          <div key={segment.key} style={{ width: `${segment.widthPct}%`, background: segment.color }} />
        ))}
      </div>
      <ul aria-label="Stages" className="divide-y divide-border text-sm">
        {segments.map((segment) => (
          <StageLegendRow key={segment.key} segment={segment} />
        ))}
      </ul>
      <p className="mt-auto text-xs text-muted-foreground">
        Still moving <Figure value={mix.stillMoving} /> · Out of play <Figure value={mix.outOfPlay} /> · of {mix.total}
      </p>
    </>
  );
}

function StageLegendRow({ segment }: { segment: StageSegmentModel }) {
  return (
    <li className="flex items-center justify-between gap-3 py-2">
      <span className="flex items-center gap-2 text-foreground">
        <span aria-hidden className="size-2 shrink-0 rounded-full" style={{ background: segment.color }} />
        {segment.label}
      </span>
      <span className="flex items-center gap-3 tabular-nums">
        <span className="text-foreground">{segment.count}</span>
        <span className="w-16 text-right text-xs text-muted-foreground">{formatSplitPercent(segment.percent, segment.count)}</span>
      </span>
    </li>
  );
}

function Figure({ value }: { value: number }) {
  return <span className="font-medium text-foreground">{value}</span>;
}
